// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation } from '@apollo/client';
import { DataGrid, type GridColDef, type GridRowSelectionModel } from '@mui/x-data-grid';
import { clientLogger } from '@seedplanner/logger';
import { utils } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from '../../lib/base';
import { CROP_NAMES_QUERY } from '../../queries/CropNamesQuery';
import { CROPS_QUERY } from '../../queries/CropsQueries';
import type { Theme } from '../../theme/Theme';
import type { MakeDefaultsMutation, MakeDefaultsMutationVariables } from '../../types/__generated__/graphql';
import { getDefaultCropsMappedForDialog } from '../../utils';
import { ExitDialogButton } from '../common/button';

const MAKE_DEFAULTS_MUTATION = gql`
  mutation MakeDefaults($selected_crops: [Int!]!) {
    addDefaultCrops(input: { selected_crops: $selected_crops }) {
      crops_created {
        id
        crop_name
        total_grow_time
        steps {
          count
        }
      }
    }
  }
`;

interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly existingCropNames: Set<string>;
}

export default function AddDefaultCropsDialog({ isOpen, onClose, onSuccess, existingCropNames }: Props) {
  const { preferredWeightUnit } = useUserContext();
  const [selected, setSelected] = React.useState<GridRowSelectionModel>([]);
  // Okay to use useMediaQuery here because this is a dialog
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const handleSelectedChange = (newSelected: GridRowSelectionModel) => {
    setSelected(newSelected);
  };

  const [makeDefaults, { loading }] = useMutation<MakeDefaultsMutation, MakeDefaultsMutationVariables>(
    MAKE_DEFAULTS_MUTATION,
  );

  const onSubmit = () => {
    void makeDefaults({
      variables: {
        selected_crops: selected.map((id) => (typeof id === 'string' ? Number.parseInt(id) : id)),
      },
      onCompleted: () => {
        enqueueSnackbar('Created new crops', {
          variant: 'success',
        });
        onSuccess();
        setSelected([]);
      },
      onError: (errorIn) => {
        clientLogger.error(errorIn);
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
      },
      refetchQueries: [{ query: CROPS_QUERY }, { query: CROP_NAMES_QUERY }],
    });
  };
  const isSubmitDisabled = loading || selected.length === 0;

  const cols: GridColDef[] = [
    { field: 'cropName', headerName: isMobile ? 'Crop' : 'Crop Name', width: 130, flex: 1, sortable: false },
    isMobile
      ? undefined
      : { field: 'yield', headerName: isMobile ? 'Yield' : 'Yield Per Tray', width: 130, flex: 1, sortable: false },
    isMobile ? undefined : { field: 'sowingDensity', headerName: 'Sow Density', width: 130, flex: 1, sortable: false },
    { field: 'growTime', headerName: 'Grow Time', width: 130, flex: 1, sortable: false },
  ].filter(utils.notNull);

  const rows = getDefaultCropsMappedForDialog(preferredWeightUnit).filter(
    (crop) => !existingCropNames.has(crop.cropName),
  );
  let form = (
    <>
      <Typography variant="body1" sx={{ px: 1, pb: 2 }}>
        Select the default crops you would like to add to your account. Default crops are meant to be a starting point
        to help you get started. You can add defaults, edit them, clone them, or create your own crops as you see fit.
      </Typography>
      <DataGrid
        rows={rows}
        columns={cols}
        checkboxSelection
        onRowSelectionModelChange={handleSelectedChange}
        rowSelectionModel={selected}
        disableColumnMenu
        disableColumnFilter
        hideFooter
        initialState={{
          sorting: {
            sortModel: [{ field: 'cropName', sort: 'asc' }],
          },
        }}
      />
    </>
  );
  if (rows.length === 0) {
    form = (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1">All default crops have been added.</Typography>
      </Box>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Add Default Crops</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>{form}</DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: 'column', alignItems: 'center' }}>
        <Button sx={{ width: '100%' }} variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (!isSubmitDisabled) {
                onSubmit();
              }
            }
          }}
          sx={{ width: '100%', mt: 1 }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
        >
          Add Defaults
        </Button>
      </DialogActions>
    </Dialog>
  );
}
